import { SplitDecision } from './types';
import { updateSplitInExistingNode, addSplitToExistingNode, createFirstSplit } from './tree-manipulation';
import { getUsedOptionsForColumnAtPath } from './decision-tree-utils';

/**
 * Navigate through a decision tree following a specific path
 */
export const navigateToPath = (node: any, path: SplitDecision[]): any => {
  if (!node || !path || !Array.isArray(path)) {
    return node;
  }

  let currentNode = node;
  
  for (const decision of path) {
    if (!decision || !currentNode || currentNode.type === 'issueFlaggerLeaf' || currentNode.type === 'strategyRecommendationLeaf') {
      break;
    }

    if (!currentNode.split) {
      break;
    }
    
    switch (decision.type) {
      case 'numerical':
        if (decision.range === 'default') {
          currentNode = currentNode.split.defaultChild;
        } else if (typeof decision.range === 'object' && currentNode.split.ranges) {
          const rangeValue = decision.range as { maxValue: number };
          const matchingRange = currentNode.split.ranges.find(
            (range: any) => range.maxValue === rangeValue.maxValue
          );
          if (matchingRange) {
            currentNode = matchingRange.childNode;
          } else {
            // If no matching range found, use default
            currentNode = currentNode.split.defaultChild;
          }
        } else {
          // Fallback to default if range is invalid
          currentNode = currentNode.split.defaultChild;
        }
        break;
        
      case 'categorical':
        if (decision.category === 'default') {
          currentNode = currentNode.split.defaultChild;
        } else if (currentNode.split.categoryMap) {
          const matchingCategory = currentNode.split.categoryMap.find(
            (cat: any) => cat.category === decision.category
          );
          if (matchingCategory) {
            currentNode = matchingCategory.childNode;
          } else {
            // If no matching category found, use default
            currentNode = currentNode.split.defaultChild;
          }
        } else {
          // Fallback to default if categoryMap is invalid
          currentNode = currentNode.split.defaultChild;
        }
        break;
        
      case 'boolean':
        if (decision.value === true && currentNode.split.trueChild) {
          currentNode = currentNode.split.trueChild;
        } else {
          currentNode = currentNode.split.falseChild;
        }
        break;
    }
  }
  
  return currentNode;
};

/**
 * Add a split to a specific path in the tree
 */
export const addSplitToSpecificPath = (
  node: any,
  targetPath: any,
  columnIndex: number,
  column: any,
  newValue: string,
  createFirstSplitFn: (leafNode: any, column: any, newValue: string, columnIndex: number, usedOptions?: string[]) => any,
  addSplitToExistingNodeFn: (node: any, column: any, newValue: string, usedOptions?: string[]) => any
): any => {
  // Get used options for this column at the specific path
  const usedOptions = getUsedOptionsForColumnAtPath(node, targetPath, columnIndex);
  
  // Handle null/undefined target path
  if (!targetPath || !targetPath.decisions) {
    return createFirstSplitFn(node, column, newValue, columnIndex, usedOptions);
  }

  // For complex nested scenarios, we need to recursively update the tree
  return addSplitToNestedPath(node, targetPath, columnIndex, column, newValue, createFirstSplitFn, addSplitToExistingNodeFn, usedOptions);
};

/**
 * Add split to nested path by recursively updating the tree
 */
const addSplitToNestedPath = (
  node: any,
  targetPath: any,
  columnIndex: number,
  column: any,
  newValue: string,
  createFirstSplitFn: (leafNode: any, column: any, newValue: string, columnIndex: number, usedOptions?: string[]) => any,
  addSplitToExistingNodeFn: (node: any, column: any, newValue: string, usedOptions?: string[]) => any,
  usedOptions?: string[]
): any => {
  if (!targetPath || !targetPath.decisions || targetPath.decisions.length === 0) {
    return createFirstSplitFn(node, column, newValue, columnIndex, usedOptions);
  }

  const [currentDecision, ...remainingDecisions] = targetPath.decisions;
  
  // Handle null decisions (skip columns that don't have decisions)
  if (currentDecision === null) {
    // If we're at the target column index, we need to add the split here
    if (targetPath.decisions.length - remainingDecisions.length - 1 === columnIndex) {
      // We're at the target column index, so add the split here
      return createFirstSplitFn(node, column, newValue, columnIndex, usedOptions);
    }
    
    // Otherwise, continue with remaining decisions
    return addSplitToNestedPath(
      node,
      { decisions: remainingDecisions },
      columnIndex,
      column,
      newValue,
      createFirstSplitFn,
      addSplitToExistingNodeFn,
      usedOptions
    );
  }
  
  if (!currentDecision) {
    return createFirstSplitFn(node, column, newValue, columnIndex, usedOptions);
  }

  // If this is a leaf node, create the first split
  if (node.type === 'issueFlaggerLeaf' || node.type === 'strategyRecommendationLeaf') {
    return createFirstSplitFn(node, column, newValue, columnIndex, usedOptions);
  }

  // If this node matches the column we want to split on, add the split here
  if (node.split && node.split.columnIndex === columnIndex) {
    return addSplitToExistingNodeFn(node, column, newValue, usedOptions);
  }

  // Navigate to the appropriate child and recursively add the split
  let updatedChild;
  
  switch (currentDecision.type) {
    case 'numerical':
      if (currentDecision.range === 'default') {
        updatedChild = addSplitToNestedPath(
          node.split.defaultChild,
          { decisions: remainingDecisions },
          columnIndex,
          column,
          newValue,
          createFirstSplitFn,
          addSplitToExistingNodeFn,
          usedOptions
        );
        return {
          ...node,
          split: {
            ...node.split,
            defaultChild: updatedChild
          }
        };
      } else if (typeof currentDecision.range === 'object' && node.split.ranges) {
        const rangeValue = currentDecision.range as { maxValue: number };
        const updatedRanges = node.split.ranges.map((range: any) => {
          if (range.maxValue === rangeValue.maxValue) {
            return {
              ...range,
              childNode: addSplitToNestedPath(
                range.childNode,
                { decisions: remainingDecisions },
                columnIndex,
                column,
                newValue,
                createFirstSplit,
                addSplitToExistingNode
              )
            };
          }
          return range;
        });
        return {
          ...node,
          split: {
            ...node.split,
            ranges: updatedRanges
          }
        };
      }
      break;
      
    case 'categorical':
      if (currentDecision.category === 'default') {
        updatedChild = addSplitToNestedPath(
          node.split.defaultChild,
          { decisions: remainingDecisions },
          columnIndex,
          column,
          newValue,
          createFirstSplitFn,
          addSplitToExistingNodeFn,
          usedOptions
        );
        return {
          ...node,
          split: {
            ...node.split,
            defaultChild: updatedChild
          }
        };
      } else if (node.split.categoryMap) {
        const updatedCategoryMap = node.split.categoryMap.map((cat: any) => {
          if (cat.category === currentDecision.category) {
            return {
              ...cat,
              childNode: addSplitToNestedPath(
                cat.childNode,
                { decisions: remainingDecisions },
                columnIndex,
                column,
                newValue,
                createFirstSplit,
                addSplitToExistingNode
              )
            };
          }
          return cat;
        });
        return {
          ...node,
          split: {
            ...node.split,
            categoryMap: updatedCategoryMap
          }
        };
      }
      break;
      
    case 'boolean':
      if (currentDecision.value === true && node.split.trueChild) {
        updatedChild = addSplitToNestedPath(
          node.split.trueChild,
          { decisions: remainingDecisions },
          columnIndex,
          column,
          newValue,
          createFirstSplitFn,
          addSplitToExistingNodeFn,
          usedOptions
        );
        return {
          ...node,
          split: {
            ...node.split,
            trueChild: updatedChild
          }
        };
      } else {
        updatedChild = addSplitToNestedPath(
          node.split.falseChild,
          { decisions: remainingDecisions },
          columnIndex,
          column,
          newValue,
          createFirstSplitFn,
          addSplitToExistingNodeFn,
          usedOptions
        );
        return {
          ...node,
          split: {
            ...node.split,
            falseChild: updatedChild
          }
        };
      }
  }

  // If we can't navigate further, create the split at the current location
  return createFirstSplit(node, column, newValue, columnIndex);
};

/**
 * Update a split in a specific path by navigating to the target node and updating it
 */
export const updateSplitInSpecificPath = (
  node: any,
  targetPath: any,
  columnIndex: number,
  column: any,
  newValue: string
): any => {
  if (!targetPath || !targetPath.decisions || targetPath.decisions.length === 0) {
    return null;
  }

  const [currentDecision, ...remainingDecisions] = targetPath.decisions;
  
  // Handle null decisions (skip columns that don't have decisions)
  if (currentDecision === null) {
    return updateSplitInSpecificPath(
      node,
      { decisions: remainingDecisions },
      columnIndex,
      column,
      newValue
    );
  }
  
  if (!currentDecision) {
    return null;
  }

  // If this is a leaf node, we can't update a split
  if (node.type === 'issueFlaggerLeaf' || node.type === 'strategyRecommendationLeaf') {
    return null;
  }

  // If this node matches the column we want to update, update it here
  if (node.split && node.split.columnIndex === columnIndex) {
    // Create a new target path with just the current decision for the update
    const currentTargetPath = { decisions: [currentDecision] };
    return updateSplitInExistingNode(node, column, newValue, currentTargetPath, columnIndex);
  }

  // Navigate to the appropriate child and recursively update the split
  let updatedChild;
  
  switch (currentDecision.type) {
    case 'numerical':
      if (currentDecision.range === 'default') {
        updatedChild = updateSplitInSpecificPath(
          node.split.defaultChild,
          { decisions: remainingDecisions },
          columnIndex,
          column,
          newValue
        );
        return {
          ...node,
          split: {
            ...node.split,
            defaultChild: updatedChild
          }
        };
      } else if (typeof currentDecision.range === 'object' && node.split.ranges) {
        const rangeValue = currentDecision.range as { maxValue: number };
        const updatedRanges = node.split.ranges.map((range: any) => {
          if (range.maxValue === rangeValue.maxValue) {
            return {
              ...range,
              childNode: updateSplitInSpecificPath(
                range.childNode,
                { decisions: remainingDecisions },
                columnIndex,
                column,
                newValue
              )
            };
          }
          return range;
        });
        return {
          ...node,
          split: {
            ...node.split,
            ranges: updatedRanges
          }
        };
      }
      break;

    case 'categorical':
      if (currentDecision.category === 'default') {
        updatedChild = updateSplitInSpecificPath(
          node.split.defaultChild,
          { decisions: remainingDecisions },
          columnIndex,
          column,
          newValue
        );
        return {
          ...node,
          split: {
            ...node.split,
            defaultChild: updatedChild
          }
        };
      } else if (node.split.categoryMap) {
        const updatedCategoryMap = node.split.categoryMap.map((cat: any) => {
          if (cat.category === currentDecision.category) {
            return {
              ...cat,
              childNode: updateSplitInSpecificPath(
                cat.childNode,
                { decisions: remainingDecisions },
                columnIndex,
                column,
                newValue
              )
            };
          }
          return cat;
        });
        return {
          ...node,
          split: {
            ...node.split,
            categoryMap: updatedCategoryMap
          }
        };
      }
      break;

    case 'boolean':
      if (currentDecision.value === true && node.split.trueChild) {
        updatedChild = updateSplitInSpecificPath(
          node.split.trueChild,
          { decisions: remainingDecisions },
          columnIndex,
          column,
          newValue
        );
        return {
          ...node,
          split: {
            ...node.split,
            trueChild: updatedChild
          }
        };
      } else if (node.split.falseChild) {
        updatedChild = updateSplitInSpecificPath(
          node.split.falseChild,
          { decisions: remainingDecisions },
          columnIndex,
          column,
          newValue
        );
        return {
          ...node,
          split: {
            ...node.split,
            falseChild: updatedChild
          }
        };
      }
      break;
  }

  return null;
};
